import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Database, BookOpen, Trophy, Users } from "lucide-react";

const FormatosPage = () => {
  const [selectedFormat, setSelectedFormat] = useState(null);
  const navigate = useNavigate();

  // Formats with their features
  const formats = [
    { 
      id: 'edison', 
      name: 'Edison Format', 
      description: 'March 2010 format - SJC Edison', 
      icon: '💡', 
      color: 'from-yellow-500 to-amber-600',
      enabled: true,
      features: [
        { name: 'Base de Datos de Cartas', icon: Database, action: () => navigate('/edison-cards') },
        { name: 'Banlist Edison', icon: BookOpen, action: () => navigate('/banlist') },
      ]
    },
    { 
      id: 'goat', 
      name: 'GOAT Format', 
      description: 'April 2005 format', 
      icon: '🐐', 
      color: 'from-green-500 to-emerald-600',
      enabled: false
    },
    { 
      id: 'hat', 
      name: 'HAT Format', 
      description: 'July 2014 format', 
      icon: '🎩', 
      color: 'from-purple-500 to-indigo-600',
      enabled: false
    },
    { 
      id: 'genesis', 
      name: 'Genesis Format', 
      description: 'Classic format', 
      icon: '🌟', 
      color: 'from-blue-500 to-cyan-600',
      enabled: false
    },
    { 
      id: 'speed', 
      name: 'Speed Duel', 
      description: 'Fast-paced format', 
      icon: '⚡', 
      color: 'from-red-500 to-orange-600',
      enabled: false
    },
    { 
      id: 'tcg', 
      name: 'TCG Standard', 
      description: 'Current format', 
      icon: '🏆', 
      color: 'from-pink-500 to-rose-600',
      enabled: false
    },
  ];

  return (
    <div className="min-h-screen bg-black pt-20" data-testid="formatos-page">
      <div className="container mx-auto px-4 py-4">
        <Link to="/" className="inline-flex items-center gap-2 text-pink-400 hover:text-pink-300 transition-colors text-sm">
          <ArrowLeft size={16} /> Volver al Inicio
        </Link>
      </div>

      <div className="container mx-auto px-4 py-6 max-w-6xl">
        <div className="text-center mb-8 md:mb-12">
          <h1 className="text-3xl md:text-4xl font-black mb-4">
            <span className="bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-500 bg-clip-text text-transparent">
              Formatos de Juego
            </span>
          </h1>
          <p className="text-gray-400 text-sm md:text-base max-w-2xl mx-auto">
            Explora los diferentes formatos de Yu-Gi-Oh! disponibles en nuestra comunidad.
          </p>
        </div>

        {/* Formats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 mb-12">
          {formats.map(format => (
            <button
              key={format.id}
              onClick={() => setSelectedFormat(selectedFormat === format.id ? null : format.id)}
              className={`glass-card rounded-xl p-4 md:p-6 text-center transition-all hover:scale-105 relative ${
                selectedFormat === format.id ? 'ring-2 ring-yellow-400' : ''
              } ${!format.enabled ? 'opacity-60' : ''}`}
              data-testid={`format-${format.id}`}
            >
              {format.enabled && (
                <div className="absolute -top-2 -right-2 px-2 py-0.5 bg-green-500 rounded-full text-[10px] font-bold text-white">
                  ACTIVO
                </div>
              )}
              <div className={`w-14 h-14 md:w-20 md:h-20 mx-auto mb-3 md:mb-4 rounded-full bg-gradient-to-br ${format.color} flex items-center justify-center shadow-lg`}>
                <span className="text-2xl md:text-4xl">{format.icon}</span>
              </div>
              <h3 className="text-base md:text-lg font-bold text-white mb-1">{format.name}</h3>
              <p className="text-xs md:text-sm text-gray-400">{format.description}</p>
            </button>
          ))}
        </div>

        {/* Selected Format Info */}
        {selectedFormat && (
          <div className="glass-card rounded-2xl p-6 md:p-8">
            {formats.find(f => f.id === selectedFormat)?.enabled ? (
              <>
                <div className="flex items-center justify-center gap-3 mb-6">
                  <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${formats.find(f => f.id === selectedFormat)?.color} flex items-center justify-center`}>
                    <span className="text-3xl">{formats.find(f => f.id === selectedFormat)?.icon}</span>
                  </div>
                  <div className="text-left">
                    <h2 className="text-2xl font-bold text-white">
                      {formats.find(f => f.id === selectedFormat)?.name}
                    </h2>
                    <p className="text-gray-400">
                      {formats.find(f => f.id === selectedFormat)?.description}
                    </p>
                  </div>
                </div>

                {/* Feature Buttons */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
                  {formats.find(f => f.id === selectedFormat)?.features?.map((feature, idx) => (
                    <button
                      key={idx}
                      onClick={feature.action}
                      className="flex items-center gap-4 p-4 bg-purple-900/30 border border-purple-700/50 rounded-xl hover:bg-purple-800/40 hover:border-yellow-500/50 transition-all group"
                      data-testid={`format-feature-${feature.name.toLowerCase().replace(/ /g, '-')}`}
                    >
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-yellow-500 to-amber-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                        <feature.icon size={24} className="text-black" />
                      </div>
                      <div className="text-left">
                        <h4 className="font-bold text-white group-hover:text-yellow-400 transition-colors">
                          {feature.name}
                        </h4>
                        <p className="text-xs text-gray-400">Click para acceder</p>
                      </div>
                      <svg className="w-5 h-5 text-gray-500 ml-auto group-hover:text-yellow-400 group-hover:translate-x-1 transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"/>
                      </svg>
                    </button>
                  ))}
                </div>

                {/* Edison Format Description */}
                {selectedFormat === 'edison' && (
                  <div className="mt-8 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl">
                    <h3 className="font-bold text-yellow-400 mb-2">Sobre Edison Format</h3>
                    <p className="text-gray-300 text-sm leading-relaxed">
                      El formato Edison hace referencia al meta de Yu-Gi-Oh! durante marzo de 2010, 
                      específicamente alrededor del Shonen Jump Championship (SJC) Edison en Nueva Jersey. 
                      Es considerado uno de los formatos más balanceados y diversos en la historia del juego, 
                      con una amplia variedad de estrategias viables incluyendo Gladiator Beasts, Lightsworn, 
                      Blackwings, y muchas más.
                    </p>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="w-16 h-16 mx-auto mb-4 bg-yellow-500/20 rounded-full flex items-center justify-center">
                  <span className="text-3xl">🚧</span>
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-yellow-400 mb-3 text-center">
                  Contenido en Desarrollo
                </h2>
                <p className="text-gray-400 text-sm md:text-base max-w-md mx-auto text-center">
                  Pronto tendremos información detallada sobre {formats.find(f => f.id === selectedFormat)?.name}, 
                  incluyendo banlist, cartas populares y estrategias.
                </p>
              </>
            )}
          </div>
        )}

        {/* Coming Soon Notice */}
        <div className="mt-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-900/30 border border-purple-700 rounded-full text-sm text-purple-300">
            <span className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></span>
            Más formatos y contenido próximamente
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormatosPage;
